import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, Subject, BehaviorSubject } from 'rxjs';
import { environment } from '@environments/environment';
import { GlobalFunctions } from '@global/GlobalFunctions';

/**
 * Interface for Account Invitation Object
 * @help
 * let invite = this.invitations.find(({ inviteGUID }) => inviteGUID === "<ID String>")
 */
export interface IAccountInvite {
	/** Account UID for the Invitation */
	AccountUID: string
	/** Name of Invited User */
	InviteName: string
	/** Email address of Invited User */
	InviteEmail: string
	/** Custom Message Sent with Invitation */
	InviteMessage: string
	/** ID of the user sending the invitation */
	InviteUserID: string
	/** URL from the server for the Email Link */
	InviteServerURL: string
	/** Invite User as an Admin */
	InviteAsAdmin:boolean
	/** Resend the Invite */
	Resend: boolean
}
/**
 * Interface for Account Invitation Object
 * @help
 * let invite = this.invitations.find(({ inviteGUID }) => inviteGUID === "<ID String>")
 */
export interface IAccountInviteDetail {
	/** ID of the Invitation */
	InviteID: number
	/** Account ID for the Invitation */
	AccountID: number
	/** ID of User Sending Invite*/
	InviteUserID: number
	/** Name of Invited User */
	InviteName: string
	/** Email address of Invited User */
	InviteEmail: string
	/** GUID of the Invitation */
	InviteGUID: string
	/** Timestamp when Invitation Sent */
	InviteDateTime: number
	/** Timestamp when Invitation Expired */
	InviteExpires: number
	/** Custom Message Sent with Invitation */
	InviteMessage: string
	/** Invite User as an Admin */
	InviteAsAdmin:boolean
	/** Status of Canceled Invitation */
	Canceled: boolean
	/** Invitation Canceled By (ID of User) */
	CanceledByID: number
	/** Invitation Canceled By */
	CanceledBy: string
	/** Reason Why Invitation was Canceled */
	CancelReason: boolean
	/** Status of Canceled Invitation */
	CancelDate: number
	/** Invitation was Accepted */
	Accepted: boolean
	/** Date the Invitation was Accepted */
	AcceptedDate: number
	/** Status of Expired Invitation */
	Expired: boolean
	/** Account GUID */
	AccountGUID:string
	/** Name of the Account for Invitation */
	AccountName: string
	/** Username of person who sent the Invite */
	Name: string
	/** Email of person who sent the Invite */
	Email: string
}

/**
 * Interface for Account Invitation Response Object
 * @help
 */
export interface IAccountInviteResponse {

	/** GUID for the new invitation */
	InviteGUID: string
	/** Name of Invited User */
	InviteName: string
	/** Email ofn invited User */
	InviteEmail: string
	/** Date Invitation Expires */
	InviteExpires: number
	/** Custom Message Sent with Invitation */
	InviteMessage: string
	/** Invite User as an Admin */
	InviteAsAdmin:boolean
	/** Account GUID */
	AccountGUID:string
	/** Name of the Account for Invitation */
	AccountName: string
	/** Response User ID */
	ResponseUserID:number
	/** Username of user sending the Invite */
	Name: string
	/** Email of user sending the Invite */
	Email: string
}

/**
 * Service
 */
@Injectable({
	providedIn: 'root'
})
export class InvitesService {

	/** Enable Console Messages */
	private Debug: boolean = false

	/**
	 * Log message to the Console
	 * @param str
	 */
	consoleLog(str: any) { if (this.Debug == true) console.log(str) }

	/** API Route */
	apiRoute: string = "api/invites"

	/**
	 * Default Constructor
	 * @param http Imports the 'HttpClient' Object for internet requests
	 * @param globalFunctions globalFunctions
	 */
	constructor(
		private http: HttpClient,
		private globalFunctions: GlobalFunctions
	) {

	}

	/**
	 * Get list of Invitations
	 * @param AccountGUID
	 * @returns
	 */
	getInvitations$(data: any): Observable<any> {
		let url = `${environment.apiUrl}/${this.apiRoute}`
		return this.globalFunctions.fetchAPI(this.http, url, "GET", data)
	}

	/**
	 * Invite New User
	 * @param AccountGUID
	 * @param invite
	 * @returns
	 */
	inviteAccountUser$(AccountGUID: string, invite: IAccountInvite): Observable<any> {
		let data = JSON.stringify(invite);
		let url = `${environment.apiUrl}/${this.apiRoute}/account/${AccountGUID}`
		return this.globalFunctions.fetchAPI(this.http, url, "POST", data)
	}

	/**
	 * Cancel Invitation
	 * @param AccountGUID
	 * @param invite
	 * @returns
	 */
	inviteCancel$(AccountGUID: string, invite: IAccountInviteDetail): Observable<any> {
		let data = JSON.stringify(invite);
		let url = `${environment.apiUrl}/${this.apiRoute}/account/${AccountGUID}/${invite.InviteGUID}`
		return this.globalFunctions.fetchAPI(this.http, url, "DELETE", data)
	}

	/**
	 * Accept an Invitation
	 * @param AccountGUID
	 * @param data
	 * @returns
	 */
	inviteAccept$(AccountGUID: string,invite:IAccountInviteResponse): Observable<any> {
		let data = JSON.stringify(invite);
		let url = `${environment.apiUrl}/${this.apiRoute}/account/${AccountGUID}/${invite.InviteGUID}/response`
		return this.globalFunctions.fetchAPI(this.http, url, "POST", data)
	}

}
